import type { StoredCartProductItem } from './storageRecovery';
import type { SessionDataType } from './contexts/SessionData';

type SimpleOrderPlacementOrderData = Omit<SessionDataType, 'changeNeeded'> & {
  paymentMethod?: 'cash' | 'bank_card';
  changeNeeded?: 'Yes' | 'No';
};

type SimpleOrderPlacementPartnerDefaults = {
  pickupAddress: string;
  pickupPoint: [number, number] | null;
  location: string;
};

/**
 * Defaults fulfilment to delivery without replacing an explicit pickup choice.
 * Unknown persisted values are treated as missing so corrupted drafts cannot
 * silently keep the legacy pickup default.
 */
export const normalizeDeliveryNeeded = (value: string | null | undefined): 'Yes' | 'No' => (
  value === 'No' ? 'No' : 'Yes'
);

/**
 * Calculates the backend's total_products_cost contract from merchandise only.
 * Promotions and delivery charges are excluded because the backend applies
 * those after it has used this value to resolve delivery pricing.
 */
export const calculateTotalProductsCost = (
  shoppingCart: Array<Pick<StoredCartProductItem, 'total_cost' | 'quantity'>>,
): number => Number(
  shoppingCart
    .reduce((total, item) => total + (item.total_cost * item.quantity), 0)
    .toFixed(2),
);

/**
 * Supplies backend-required values for the temporary wholesale checkout while
 * preserving customer-entered identity, comments, and the currently resolved
 * channel. The partner pickup point acts as the hidden delivery destination.
 */
export const applySimpleOrderPlacementDefaults = <TOrderData extends SimpleOrderPlacementOrderData>(
  orderData: TOrderData,
  partnerDefaults: SimpleOrderPlacementPartnerDefaults,
): TOrderData => ({
  ...orderData,
  contacts: {
    ...(orderData.contacts ?? {}),
    preferableMethod: 'phone',
  },
  countryCode: '+382',
  phone: '00000000',
  deliveryNeeded: 'Yes',
  paymentMethod: 'cash',
  changeNeeded: 'No',
  changeAmount: null,
  deliveryInTime: 'No',
  time: null,
  deliveryAddress: partnerDefaults.pickupAddress,
  loc_latitude: partnerDefaults.pickupPoint?.[0] ?? null,
  loc_longitude: partnerDefaults.pickupPoint?.[1] ?? null,
  location: partnerDefaults.location,
  loyalty_card_number: null,
} as TOrderData);
